import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkle } from "lucide-react";
import { MaskedLine, EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1770721478216-3e5dbbe8dcc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBnb2xkJTIwamV3ZWxyeSUyMG1vZGVsJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg2MDc4OTM0fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 md:pt-40" data-testid="hero-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 md:px-10 lg:grid-cols-12">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mb-8 flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-golddark"
            data-testid="hero-overline"
          >
            <Sparkle size={14} strokeWidth={1.5} /> Fine jewellery · Botanically inclined
          </motion.p>
          <h1 className="font-serif text-6xl leading-[0.98] tracking-tighter text-ink sm:text-7xl lg:text-[7.5rem]" data-testid="hero-heading">
            <MaskedLine delay={0.35}>Where gold</MaskedLine>
            <MaskedLine delay={0.5}>learns to</MaskedLine>
            <MaskedLine delay={0.65}>
              <em className="font-light italic text-golddark">grow.</em>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.15, ease: EASE }}
            className="mt-8 max-w-md text-base leading-relaxed text-smoke"
            data-testid="hero-subtext"
          >
            Leaf-motif rings, amethyst pendants and heirloom silhouettes —
            consciously crafted in sterling silver and warm gold finishes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/catalogue"
              data-testid="hero-cta-catalogue"
              className="rounded-full bg-ink px-8 py-4 text-xs uppercase tracking-[0.25em] text-ivory transition-colors duration-500 hover:bg-golddark"
            >
              Explore the Collection
            </Link>
            <a
              href="#manifesto"
              data-testid="hero-cta-story"
              className="gold-underline flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-ink"
            >
              Our Story <ArrowDown size={14} strokeWidth={1.5} />
            </a>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, delay: 0.8, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-t-full border border-gold/50" aria-hidden="true" />
            <div className="relative aspect-[3/4] overflow-hidden rounded-t-full bg-sand">
              <motion.img
                src={HERO_IMG}
                alt="Model wearing kanakleaf gold jewellery"
                style={{ y: imgY, scale: imgScale }}
                className="h-full w-full object-cover will-change-transform"
                data-testid="hero-image"
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.6 }}
              className="absolute -left-6 bottom-10 -rotate-90 text-[10px] uppercase tracking-[0.4em] text-smoke max-lg:hidden"
            >
              Est. in nature — N°01
            </motion.p>
          </motion.div>
        </div>
      </div>
      <div className="h-24 md:h-32" />
    </section>
  );
};
